import React, { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { Input } from "@abbiamo/ui";
import type { PortalSeller } from "../portal-users.types";

// Acima desse total, achar uma filial rolando a lista vira inviável — mostra
// a busca. Abaixo, a lista já cabe na área visível sem precisar filtrar.
const SEARCH_THRESHOLD = 8;

// Busca acento-insensível: "guaruja" precisa casar com "Guarujá".
const stripAccents = (value: string) => value.normalize("NFD").replace(/[̀-ͯ]/g, "");

/**
 * Seleção de filiais de uma operação. Usado pelos modais de convite, de
 * adição de vínculo e de edição de vínculo — os três concedem filiais e
 * precisam se comportar igual.
 *
 * `selectedIds` a null significa "ainda carregando"; a lista vazia é um
 * estado válido (um vínculo pode existir sem filial).
 */
export const PortalSellerPicker = ({
	sellers,
	selectedIds,
	onChange,
	label = "Filiais com acesso",
	hint = "Selecione as filiais que este usuário pode acessar.",
	emptyLabel = "Nenhuma filial disponível nesta operação.",
	loadingLabel = "Carregando filiais…",
	footer,
}: {
	sellers: PortalSeller[];
	selectedIds: string[] | null;
	onChange: (sellerIds: string[]) => void;
	label?: string;
	hint?: string;
	emptyLabel?: string;
	loadingLabel?: string;
	footer?: React.ReactNode;
}) => {
	const [query, setQuery] = useState("");

	const visibleSellers = useMemo(() => {
		const normalizedQuery = stripAccents(query.trim().toLowerCase());
		if (!normalizedQuery) return sellers;
		return sellers.filter((seller) =>
			stripAccents(seller.trading_name.toLowerCase()).includes(normalizedQuery),
		);
	}, [sellers, query]);

	const selected = selectedIds ?? [];
	const showSearch = sellers.length > SEARCH_THRESHOLD;
	const isFiltering = showSearch && query.trim().length > 0;
	const allVisibleSelected =
		visibleSellers.length > 0 &&
		visibleSellers.every((seller) => selected.includes(seller.id));

	const toggleSeller = (id: string) =>
		onChange(
			selected.includes(id)
				? selected.filter((sellerId) => sellerId !== id)
				: [...selected, id],
		);

	const toggleAllVisible = () => {
		const visibleIds = visibleSellers.map((seller) => seller.id);
		onChange(
			allVisibleSelected
				? selected.filter((id) => !visibleIds.includes(id))
				: Array.from(new Set([...selected, ...visibleIds])),
		);
	};

	return (
		<div>
			{label ? (
				<p className="mb-ab-1 text-ab-sm font-ab-medium text-ab-fg">{label}</p>
			) : null}
			{hint ? <p className="mb-ab-3 text-ab-xs text-ab-fg-muted">{hint}</p> : null}
			{showSearch && selectedIds !== null && sellers.length > 0 ? (
				<label className="relative mb-ab-2 flex items-center">
					<Search
						size={14}
						className="pointer-events-none absolute left-ab-3 text-ab-fg-muted"
						aria-hidden
					/>
					<Input
						aria-label="Buscar filial"
						placeholder="Buscar filial"
						value={query}
						onChange={(event) => setQuery(event.target.value)}
						className="h-ab-ctl w-full pl-ab-8"
					/>
				</label>
			) : null}
			<div className="ab-scroll max-h-60 overflow-y-auto overflow-x-hidden bg-eui-card" data-ui="block">
				{selectedIds === null ? (
					<div className="p-ab-3">
						<PortalSellerPickerLoading label={loadingLabel} />
					</div>
				) : sellers.length === 0 ? (
					<p className="p-ab-3 text-ab-xs text-ab-fg-muted">{emptyLabel}</p>
				) : visibleSellers.length === 0 ? (
					<p className="p-ab-3 text-ab-xs text-ab-fg-muted">
						Nenhuma filial encontrada para &quot;{query.trim()}&quot;.
					</p>
				) : (
					<>
						<label className="sticky top-0 z-10 flex cursor-pointer items-center gap-ab-3 border-b border-ab-border bg-eui-card px-ab-3 py-ab-2 text-ab-sm font-ab-medium text-ab-fg select-none">
							<input
								type="checkbox"
								className="h-4 w-4 flex-shrink-0"
								checked={allVisibleSelected}
								onChange={toggleAllVisible}
							/>
							<span>{isFiltering ? "Selecionar visíveis" : "Selecionar todas"}</span>
							<span className="ml-auto text-ab-xs font-ab-medium tabular-nums text-ab-fg-muted">
								{isFiltering
									? `${selected.length}/${sellers.length} · ${visibleSellers.length} visíveis`
									: `${selected.length}/${sellers.length}`}
							</span>
						</label>
						{visibleSellers.map((seller) => (
							<label
								key={seller.id}
								className={`flex cursor-pointer items-center gap-ab-3 border-b border-ab-border px-ab-3 py-ab-2 text-ab-sm text-ab-fg last:border-0 hover:bg-ab-muted/40 select-none ${
									selected.includes(seller.id) ? "bg-eui-accent-tint/60 text-ab-fg" : ""
								}`}
							>
								<input
									type="checkbox"
									className="h-4 w-4 flex-shrink-0"
									checked={selected.includes(seller.id)}
									onChange={() => toggleSeller(seller.id)}
								/>
								<span>{seller.trading_name}</span>
							</label>
						))}
					</>
				)}
			</div>
			{footer}
		</div>
	);
};

const PortalSellerPickerLoading = ({ label }: { label: string }) => (
	<div className="flex items-center gap-ab-2 text-ab-xs text-ab-fg-muted">
		<span className="h-3 w-3 animate-spin rounded-full border-2 border-ab-border border-t-ab-fg" />
		{label}
	</div>
);
